/**
 * Google OAuth callback (V1 auth-profiles-gdpr, #80a) — the
 * `${appOrigin}/auth/google/callback` destination of the PKCE round-trip.
 * POST /auth/google exchanges the code server-side; 412 DOB_REQUIRED (new
 * account without a stored DOB) routes back through the S-02 gate first —
 * the age gate is never skippable, whatever the door.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resolveCopy } from "@humble/shared";
import { Card, IconBrokenHeart } from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import { setSession, useStore } from "../../lib/store.js";
import {
  browserTz,
  clearSignupFlow,
  routeForLifecycle,
  signupFlow,
  writeUnderageLockRef,
} from "./signup-flow.js";
import { takeGoogleVerifier } from "./google-pkce.js";
import { UnderageLock } from "./underage-lock.js";
import "./auth.css";

type Phase = "working" | "verify_email" | "dob_required" | "failed" | "underage";

export function GoogleCallbackScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dob = useStore(signupFlow, (s) => s.dob);
  const [phase, setPhase] = useState<Phase>("working");
  const [error, setError] = useState<string | null>(null);
  const [lockRef, setLockRef] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const code = params.get("code");
    const verifier = takeGoogleVerifier();
    if (!code || !verifier) {
      setPhase("failed");
      setError(resolveCopy("auth.oauth_fail") ?? "Google sign-in failed.");
      return;
    }
    (async () => {
      try {
        const body: {
          code: string;
          code_verifier: string;
          device_fp: string;
          dob?: string;
          tz?: string;
        } = { code, code_verifier: verifier, device_fp: getDeviceFp() };
        if (dob) {
          body.dob = dob;
          body.tz = browserTz();
        }
        const pair = await api.post("/auth/google", { body });
        if (pair && "access_token" in pair && pair.access_token) {
          clearSignupFlow();
          setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
          navigate(routeForLifecycle(pair.lifecycle));
          return;
        }
        // 202: Google email unverified — the classic verify flow takes over.
        setPhase("verify_email");
      } catch (err) {
        if (isApiError(err, "DOB_REQUIRED")) {
          setPhase("dob_required");
        } else if (isApiError(err, "UNDERAGE")) {
          const ref = typeof err.details?.lock_ref === "string" ? err.details.lock_ref : "unavailable";
          writeUnderageLockRef(ref);
          setLockRef(ref);
          setPhase("underage");
        } else {
          setPhase("failed");
          setError(
            isApiError(err) ? err.message : (resolveCopy("auth.oauth_fail") ?? "Google sign-in failed."),
          );
        }
      }
    })();
  }, [params, dob, navigate]);

  if (phase === "underage") return <UnderageLock lockRef={lockRef} />;

  return (
    <div className="auth auth--center" data-screen="S-04-google">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        {phase === "working" && <p role="status">Talking to Google…</p>}
        {phase === "verify_email" && (
          <>
            <h1 className="auth__title">One more step</h1>
            <p className="text-dim">
              Google couldn't vouch for that email address, so we sent a verification mail
              instead. Click it and you're in.
            </p>
            <Link to="/verify-email" className="btn btn--primary btn--block">
              Continue
            </Link>
          </>
        )}
        {phase === "dob_required" && (
          <>
            <h1 className="auth__title">Date of birth first</h1>
            <p className="text-dim">
              New account — Humble needs your date of birth before anything else. It takes ten
              seconds, then hit the Google button again.
            </p>
            <Link to="/signup/age" className="btn btn--primary btn--block">
              Enter date of birth
            </Link>
          </>
        )}
        {phase === "failed" && (
          <>
            <h1 className="auth__title">Google said no</h1>
            <p className="field__error" role="alert">
              {error}
            </p>
            <Link to="/" className="btn btn--primary btn--block">
              Back to sign in
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
